/**
 * ChoresPage — the household task list.
 *
 * Residence Journal · chief-of-staff mode. Presentation only —
 * every handler, testid, route and state hook is preserved.
 */
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { WeeklyResetModal, useWeeklyReset } from "./WeeklyReset";
import { HomeOSTaskBoard } from "./components/HomeOSTaskBoard";
import { TaskEditModal } from "./components/TaskEditModal";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

const SCOPE_OPTIONS = [
  { id: "chores", label: "Chores only" },
  { id: "all",    label: "All household tasks" },
];

export default function ChoresPage() {
  const weeklyReset = useWeeklyReset();
  const { phase } = useLightPhase();
  const [scope, setScope] = useState("chores");
  const [editingTask, setEditingTask] = useState(null);
  const { tasks: householdTasks, updateTask, removeTask } = useHomeOSState();

  const scopedTasks = useMemo(() => {
    if (scope === "chores") return householdTasks.filter(t => t.category === "Chores");
    return householdTasks;
  }, [scope, householdTasks]);

  const stats = useMemo(() => ({
    total:       scopedTasks.length,
    needsAction: scopedTasks.filter(t => t.status === "Needs action").length,
    aiSuggested: scopedTasks.filter(t => t.status === "Ara suggested").length,
    done:        scopedTasks.filter(t => t.status === "Done").length,
  }), [scopedTasks]);

  const focal = stats.total === 0
    ? "Nothing on the list. Add the first chore whenever you're ready."
    : stats.needsAction === 0
      ? "Everything is quietly on track."
      : stats.needsAction === 1
        ? "One task is asking for someone's hand."
        : `${stats.needsAction} tasks are asking for someone's hand.`;

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="module-page-chores">
      <PageHeading
        testid="chores-hero"
        module="family"
        room="Chores"
        title="The chore list"
        focal={focal}
        actions={
          <>
            <PillButton
              variant="solid"
              onClick={() => setEditingTask({ taskId: null, mode: "add" })}
              testid="add-task-btn"
            >
              Add task
            </PillButton>
            <TextLink onClick={() => weeklyReset.start()} testid="chores-open-weekly-reset">
              Weekly reset
            </TextLink>
          </>
        }
      />

      {/* Preserved testids (hidden shims) */}
      <div className="hidden" data-testid="module-title" aria-hidden>Chores</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Fair, simple household tasks without constant reminding.</div>

      {/* ── Snapshot — small stat panels appear once tasks exist ────── */}
      {stats.total > 0 && (
      <EditorialSection
        testid="chores-stats"
        moduleMark={{ module: 'family', label: 'This week' }}
        title="How the list is sitting"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5" data-testid="stat-total">
          <StatLine label="Total" value={stats.total} testid="stat-total" />
          <StatLine label="Needs action" value={stats.needsAction} testid="stat-needs-action" />
          <StatLine label="Ara suggested" value={stats.aiSuggested} testid="stat-ai-suggested" />
          <StatLine label="Done" value={stats.done} testid="stat-done" />
        </div>
      </EditorialSection>
      )}

      {/* ── Scope toggle + task board ─────────────────────────────── */}
      <EditorialSection
        testid="chores-scope-toggle"
        moduleMark={{ module: 'family', label: 'The list' }}
        title={scope === "chores" ? "Chores only" : "Every household task"}
        aside={
          <div className="flex flex-col items-start gap-3">
            {SCOPE_OPTIONS.map(opt => {
              const active = scope === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setScope(opt.id)}
                  className="text-[13px] transition-ara"
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    color: active ? 'var(--ara-ink)' : 'var(--ara-ink-soft)',
                    fontWeight: active ? 500 : 400,
                    borderBottom: active ? '1px solid var(--ara-ink)' : '1px solid transparent',
                    paddingBottom: '2px',
                  }}
                  aria-pressed={active}
                  data-testid={`chores-scope-${opt.id}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        }
      >
        {scopedTasks.length === 0 ? (
          <div>
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{
                fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                color: 'var(--ara-ink)',
                fontWeight: 400,
                maxWidth: '32ch',
              }}
              data-testid="chores-empty-sentence"
            >
              {scope === "chores"
                ? "Every household starts here. Add the first chore."
                : "Nothing on the household list yet."}
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => setEditingTask({ taskId: null, mode: "add" })}
                testid="chores-add-first"
              >
                <Plus className="w-3 h-3" strokeWidth={1.5} /> Add the first task
              </PillButton>
            </div>
          </div>
        ) : (
          <HomeOSTaskBoard
            tasks={scopedTasks}
            emptyHint=""
            onEditTask={(task) => setEditingTask({ taskId: task.id, mode: "edit" })}
            onDeleteTask={(task) => {
              if (window.confirm(`Remove "${task.title}" from Home OS?`)) {
                removeTask(task.id);
              }
            }}
            onMarkDone={(task) => updateTask(task.id, { status: "Done" })}
            onPause={(task, nextStatus) => updateTask(task.id, { status: nextStatus })}
          />
        )}
      </EditorialSection>

      {/* ── Weekly Reset — quiet invitation, not a coloured banner ── */}
      <EditorialSection
        testid="chores-weekly-reset"
        moduleMark={{ module: 'family', label: 'Weekly reset' }}
        title="A quiet plan for the week"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)',
            fontWeight: 400,
            maxWidth: '44ch',
          }}
        >
          One four-step flow — food, calendar, chores, money, school, supplies and family wellbeing.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
          <PillButton
            variant="solid"
            onClick={() => weeklyReset.start()}
            testid="chores-start-weekly-reset"
          >
            Start weekly reset
          </PillButton>
          <span
            className="text-[12px]"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
          >
            Takes about two minutes
          </span>
        </div>
      </EditorialSection>

      {/* ── How Ara helps — no coloured "AI can help" tile ─────────── */}
      <EditorialSection
        testid="ai-help-section"
        moduleMark={{ module: 'family', label: 'Ara' }}
        title="How Ara helps"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)',
            fontWeight: 400,
            maxWidth: '48ch',
          }}
        >
          Ara can assign chores by age, availability, energy, fairness and urgency — quietly, without reminding.
        </p>
      </EditorialSection>

      <Colophon>ARA · Chores</Colophon>

      <WeeklyResetModal open={weeklyReset.open} onClose={weeklyReset.close} />
      <TaskEditModal
        open={!!editingTask}
        taskId={editingTask?.taskId}
        mode={editingTask?.mode || "edit"}
        onClose={() => setEditingTask(null)}
      />
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

/* Small statistic line — Inter, tabular, quiet. */
function StatLine({ label, value, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <p
        className="text-[11px] leading-tight"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--ara-ink-soft)',
          fontWeight: 500,
        }}
      >
        {label}
      </p>
      <p
        className="mt-2 text-[26px] leading-none tabular-nums"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          color: 'var(--ara-ink)',
          fontWeight: 400,
        }}
      >
        {value}
      </p>
    </div>
  );
}
